import React, { useRef } from 'react';
import { toast } from 'react-toastify';
import { getTranslateByKey, isNullUndefined } from '@/main/services/utils';
import KOGDragDropArea from '../dragDrop/dragDropArea';
import KOGDragDropScreen from '../dragDrop/dragDropScreen';

export type ITypeFile =
  | 'image'
  | 'file'
  | 'excel'
  | 'default'
  | 'json'
  | 'invoices'
  | 'pdf'
  | 'xlsx'
  | 'xml'
  | 'document'
  | 'docx'
  | 'email';

const OBJ_FILES: { [key in ITypeFile]: string } = {
  image: '.png, .jpg, .jpeg',
  file: '.xls, .xlsx, .sheet, .csv, .pdf, .doc, .docx, .txt, .msg, .eml',
  excel: '.xls, .xlsx, .sheet, .csv',
  invoices: '.pdf, .xml',
  json: '.json',
  pdf: '.pdf',
  xlsx: '.xlsx',
  xml: '.xml',
  document: '.pdf, .doc, .docx, .rtf, .odt, .txt, .msg, .eml',
  docx: '.docx',
  email: '.msg, .eml',
  default: ''
};
const OBJ_FILES_INPUT: { [key in ITypeFile]: string } = {
  image: 'image/*',
  file: 'application/*, message/rfc822, application/vnd.ms-outlook',
  excel:
    'application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  invoices: 'application/pdf, text/xml',
  json: 'application/json',
  pdf: 'application/pdf',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  xml: 'text/xml',
  document:
    'application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/rtf, application/vnd.oasis.opendocument.text, text/plain, message/rfc822, application/vnd.ms-outlook',
  docx:
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  email: 'message/rfc822, application/vnd.ms-outlook',
  default: '*/*'
};

export type IFileExtension =
  | 'jpg'
  | 'jpeg'
  | 'png'
  | 'pdf'
  | 'txt'
  | 'xml'
  | 'xls'
  | 'xlsx'
  | 'sheet'
  | 'csv'
  | 'doc'
  | 'docx'
  | 'json'
  | 'odt'
  | 'rtf'
  | 'msg'
  | 'eml'
  | 'mbox'
  | 'emlx';

function typeFileAcceptInput(typeFiles: ITypeFile[] | ITypeFile | undefined) {
  if (!typeFiles) return '*/*';
  if (Array.isArray(typeFiles))
    return typeFiles.map(type => OBJ_FILES_INPUT[type]).join(',');

  return OBJ_FILES_INPUT[typeFiles];
}

function typeFileAccept(typeFiles: ITypeFile[] | ITypeFile | undefined) {
  if (!typeFiles) return null;
  if (Array.isArray(typeFiles))
    return typeFiles.map(type => OBJ_FILES[type]).join(',');

  return OBJ_FILES[typeFiles];
}
function isFileExtensionValid(fileType: ITypeFile, fileName: string): boolean {
  if (fileType === 'default') return true;
  const validTypes = typeFileAccept(fileType);
  if (!validTypes) return true;

  const allowedExtensions = validTypes.split(',').map(ext => ext.trim());
  const fileExtension = fileName.slice(fileName.lastIndexOf('.')).toLowerCase();
  return allowedExtensions.includes(fileExtension);
}
interface IInputDragDropUploaderProps {
  files: File[];
  typeFiles?: ITypeFile[] | ITypeFile;
  maxFileByType?: { [key in IFileExtension]?: number };
  maxFiles?: number | null;
  resetFiles?: boolean;
  isFullScreenDragDrop?: boolean;
  customTextDrop?: string;
  isRequired?: boolean;
  maxTotalSize?: number;
  onFilesChange: (files: File[]) => void;
  onCustomDropAction?: (event: React.DragEvent<HTMLDivElement>) => void;
  maxFileSize?: number;
}
const KOGInputDragDropUploader = ({
  files,
  typeFiles,
  maxFileByType,
  resetFiles = false,
  isFullScreenDragDrop = false,
  maxFiles,
  customTextDrop,
  maxTotalSize,
  isRequired = false,
  maxFileSize,
  onFilesChange,
  onCustomDropAction = () => {}
}: IInputDragDropUploaderProps) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const validTypesInput = typeFileAcceptInput(typeFiles);

  function validateFiles(selectedFiles: File[]): File[] {
    let invalidFiles: string[] = [];
    let repeatedFiles: string[] = [];
    let repeatedFilesByType: string[] = [];
    const totalFiles = resetFiles
      ? selectedFiles.length
      : files.length + selectedFiles.length;

    if (!isNullUndefined(maxFiles)) {
      if (totalFiles > maxFiles) {
        toast.error(
          `${getTranslateByKey(
            'information.maximumNumberFilesExceeded',
            true
          )}: ${maxFiles}`
        );
        return [];
      }
    }

    if (maxFileSize) {
      let hasErrorFileSize = false;
      selectedFiles.forEach(file => {
        if (file.size / (1024 * 1024) > maxFileSize) {
          toast.error(
            getTranslateByKey('claimFile.claimMaxSizeFile', true).replace(
              '{0}',
              String(maxFileSize)
            )
          );

          hasErrorFileSize = true;
        }
      });

      if (hasErrorFileSize) return [];
    }

    if (maxTotalSize) {
      const currentTotalSize = files.reduce(
        (total, file) => total + file.size,
        0
      );
      const newFilesTotalSize = selectedFiles.reduce(
        (total, file) => total + file.size,
        0
      );
      const totalSizeInMB =
        (currentTotalSize + newFilesTotalSize) / (1024 * 1024);

      if (totalSizeInMB > maxTotalSize) {
        toast.error(
          getTranslateByKey('claimFile.claimMaxSizeFile', true).replace(
            '{0}',
            String(maxTotalSize)
          )
        );
        return [];
      }
    }

    const validFiles = selectedFiles.filter(file => {
      if (!isFileExtensionValid(typeFiles as ITypeFile, file.name)) {
        invalidFiles.push(file.name);
        return false;
      }
      if (files.some(f => f.name === file.name)) {
        repeatedFiles.push(file.name);
        return false;
      }

      if (maxFileByType) {
        const typeFile = file.name.split('.')[1];

        const maxFile = maxFileByType[typeFile.toLowerCase() as IFileExtension];
        const grupFiles = [...files, ...selectedFiles];
        const filesByType = grupFiles.filter(
          f =>
            f.name.split('.')[1].toLowerCase() ===
            file.name.split('.')[1].toLowerCase()
        );

        if (maxFile && filesByType.length > maxFile) {
          repeatedFilesByType.push(file.name);
          return false;
        }
      }
      return true;
    });

    if (repeatedFilesByType.length)
      toast.error(
        `${getTranslateByKey(
          'information.validateUploadFileToInvoiceWithTheSameFileFormat',
          true
        )}`
      ); //Erro mensage
    if (repeatedFiles.length)
      toast.error(
        `${getTranslateByKey(
          'information.fileSameName',
          true
        )}: ${repeatedFiles.join(', ')}`
      );
    if (invalidFiles.length)
      toast.error(
        `${getTranslateByKey(
          'importInventory.invalidFileFormat',
          true
        )}: ${invalidFiles.join(', ')}`
      );
    return validFiles;
  }
  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    onCustomDropAction(event);
    const selectedFiles = Array.from(event.dataTransfer.files);
    const validFiles = validateFiles(selectedFiles);
    onFilesChange(validFiles);
  };

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!event.target.files) return;
    const selectedFiles = Array.from(event.target.files);
    const validFiles = validateFiles(selectedFiles);
    onFilesChange(validFiles);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const RenderFullDragDrop: React.FC = ({ children }) => {
    if (!isFullScreenDragDrop) return <>{children}</>;

    return (
      <KOGDragDropScreen onCustomDrop={handleDrop}>
        {children}
      </KOGDragDropScreen>
    );
  };

  return (
    <RenderFullDragDrop>
      <KOGDragDropArea fileInputRef={fileInputRef} onCustomDrop={handleDrop}>
        <p>
          {!isNullUndefined(customTextDrop)
            ? customTextDrop
            : getTranslateByKey('importInventory.chooseOrDropFile')}

          {isRequired && <span style={{ color: 'red', marginLeft: 5 }}>*</span>}
        </p>
        <input
          type="file"
          accept={validTypesInput}
          multiple
          ref={fileInputRef}
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
        />
      </KOGDragDropArea>
    </RenderFullDragDrop>
  );
};

export default KOGInputDragDropUploader;
